import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import prettyBytes from 'pretty-bytes';

import { FILE_STORAGE } from '@/config/env';
import { ApiErrorResponse } from '@/features/api';

import { FileAttachment } from '../types/FileAttachment';
import { FileAttachmentCallbacks } from '../types/FileAttachmentCallbacks';
import { FileUploadStatus } from '../types/FileUploadStatus';
import { OcrFileViewModel } from '../types/OcrFileViewModel';
import { UploadedFileViewModel } from '../types/UploadedFileViewModel';

type UseFileAttachmentsProps = {
  /** Callbacks for fetching and viewing attachments. */
  callbacks: FileAttachmentCallbacks;
  /** Extensions/MIME types that are accepted for upload. If empty, all files are accepted. */
  validFileExtensions: ReadonlyArray<string>;
  /** Whether to allow file uploads. */
  allowFileUpload: boolean;
  /** Maximum number of uploaded files permitted. */
  maxUploadsCount: number;
  /** Previously uploaded files to display. */
  uploadedFilesInitialValue: Array<UploadedFileViewModel>;
  /** OCR file to display. */
  ocrFile: OcrFileViewModel;
  /**
   * When `true`, attachments will be fetched on component mount. Specify `false` when you want to
   * prevent that for lazy-loading, e.g. until a menu is opened, or while an entity is created on
   * the server (like a payment request).
   */
  shouldFetchAttachments: boolean;
  /** Callback to notify when an arbitrary field has changed. */
  onFieldChanged: (fieldName: string, fieldValue: unknown) => void;
};

/**
 * Hook which underpins attachment upload components.
 */
const useFileAttachments = ({
  allowFileUpload,
  callbacks,
  maxUploadsCount,
  ocrFile,
  shouldFetchAttachments,
  uploadedFilesInitialValue,
  validFileExtensions,
  onFieldChanged,
}: UseFileAttachmentsProps) => {
  const instanceId = useId();

  const intl = useIntl();
  const confirm = useConfirm();

  const {
    data: attachments,
    error: attachmentsError,
    isFetching: attachmentsIsLoading,
    refetch: refetchAttachments,
    isSuccess: attachmentsIsSuccess,
  } = useQuery({
    queryKey: ['attachments', instanceId],
    queryFn: () => callbacks.getAttachments(),
    enabled: shouldFetchAttachments,
  });

  const prettyMaxFileSize = useMemo(
    () => prettyBytes(FILE_STORAGE.MAX_UPLOAD_SIZE_BYTES, { locale: intl.locale }),
    [intl.locale]
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Array<UploadedFileViewModel>>(
    uploadedFilesInitialValue ?? []
  );

  const {
    mutate: uploadAttachments,
    isLoading: uploadAttachmentsIsLoading,
    isSuccess: uploadAttachmentsIsSuccess,
  } = useMutation({
    mutationFn: (fileList: FileList) => callbacks.filesSelected(fileList),
    onMutate: () => {
      // Notify the callback that upload started
      if (callbacks.fileUploadStatusChanged) {
        callbacks.fileUploadStatusChanged(FileUploadStatus.Uploading);
      }
    },
    onSettled: () => {
      // Notify the callback that upload finished
      if (callbacks.fileUploadStatusChanged) {
        callbacks.fileUploadStatusChanged(FileUploadStatus.Uploaded);
      }
    },
    onSuccess: (uploadedFile) => {
      setUploadedFiles((prev) => {
        const next = [...prev, uploadedFile];
        callbacks.filesUploaded?.(next);
        return next;
      });
    },
    onError: (error: ApiErrorResponse) => {
      // Console error is used for historical reasons
      // eslint-disable-next-line no-console
      console.error('Upload error:', error);

      confirm({
        title: intl.formatMessage({ defaultMessage: 'Error uploading file' }),
        description: error?.type
          ? intl.formatMessage(
              {
                defaultMessage:
                  'There was an error uploading this file: {errorType}. Please try again later.',
              },
              { errorType: error.type }
            )
          : intl.formatMessage({
              defaultMessage:
                'There was an unexpected error uploading this file. Please try again later.',
            }),
        hideCancelButton: true,
      });
    },
  });

  const { mutate: deleteAttachment, isLoading: deleteAttachmentIsLoading } = useMutation({
    mutationFn: (uploadedFile: UploadedFileViewModel) => {
      /*
      `fileDeleted` returns a boolean, so we need to update state here, instead of in `onSuccess`.

      Unfortunately, that means if there's a failure, the state will be out of sync with the server;
      however, current business rules allow for that in order to allow the user to continue making a
      payment without being stuck with an attachment they can't remove.
      */
      setUploadedFiles((prev) => {
        const next = prev.filter(
          (file) => file.companyFileStorageFileId !== uploadedFile.companyFileStorageFileId
        );
        callbacks.filesUploaded?.(next);
        return next;
      });

      return callbacks.fileDeleted(uploadedFile);
    },
    onError: (error: ApiErrorResponse) => {
      // Console error is used for historical reasons
      // eslint-disable-next-line no-console
      console.error('delete error:', error);

      // At the moment we will silently fail if file could not be removed from the server
    },
  });

  useEffect(() => {
    if (attachmentsIsSuccess || uploadAttachmentsIsSuccess) {
      onFieldChanged?.('hasAttachment', attachments?.length > 0 || uploadedFiles.length > 0);
    }
  }, [
    attachments,
    uploadedFiles,
    onFieldChanged,
    attachmentsIsSuccess,
    uploadAttachmentsIsSuccess,
  ]);

  const handleAttachmentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList.length < 1) {
      return;
    }

    if (fileList[0].size > FILE_STORAGE.MAX_UPLOAD_SIZE_BYTES) {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      confirm({
        title: intl.formatMessage({ defaultMessage: 'Upload size limit exceeded!' }),
        description: intl.formatMessage(
          { defaultMessage: 'Please upload attachments that are less than {maxUploadSize} each.' },
          { maxUploadSize: prettyMaxFileSize }
        ),
        hideCancelButton: true,
      });

      return;
    }

    uploadAttachments(fileList);
  };

  const handleConfirmUpload = useCallback(() => {
    callbacks.confirmUploadCallback(uploadedFiles);

    // Clear the list of uploaded files
    setUploadedFiles([]);
  }, [callbacks, uploadedFiles]);

  const handleViewUploadedFile = useCallback(
    (file: UploadedFileViewModel) => {
      callbacks.viewUploadedFile(file);
    },
    [callbacks]
  );

  const handleViewAttachment = useCallback(
    (attachment: FileAttachment) => {
      callbacks.viewAttachment(attachment);
    },
    [callbacks]
  );

  const handleViewOcrAttachment = useCallback(
    (documentFileId: string) => {
      callbacks.viewOcrAttachment(documentFileId);
    },
    [callbacks]
  );

  /** File types accepted by the `file` input */
  const validFileTypes = useMemo(
    () => (validFileExtensions ? validFileExtensions.join(',') : undefined),
    [validFileExtensions]
  );

  const isMaxFilesUploaded = uploadedFiles.length >= maxUploadsCount;

  const displayConfirmButton = callbacks.confirmUploadCallback != null && uploadedFiles.length > 0;

  const hasAttachmentsToDisplay = useMemo(
    () =>
      ocrFile != null ||
      attachments?.length > 0 ||
      allowFileUpload ||
      (!allowFileUpload && uploadedFiles?.length > 0),
    [ocrFile, attachments, allowFileUpload, uploadedFiles]
  );

  return {
    attachments,
    attachmentsError,
    attachmentsIsLoading,
    attachmentsIsSuccess,
    refetchAttachments,
    prettyMaxFileSize,
    fileInputRef,
    uploadedFiles,
    isUploadingAttachments: uploadAttachmentsIsLoading,
    deleteAttachment,
    deleteAttachmentIsLoading,
    handleAttachmentUpload,
    handleConfirmUpload,
    handleViewUploadedFile,
    handleViewAttachment,
    handleViewOcrAttachment,
    isMaxFilesUploaded,
    validFileTypes,
    displayConfirmButton,
    hasAttachmentsToDisplay,
  };
};

export { useFileAttachments, UseFileAttachmentsProps };
